/*
 * Токенний пошук: панель підказок + конвеєр варіантів запиту.
 *
 * Конвеєр (кожен наступний прохід — лише якщо попередній не дав товарів):
 *   1. запит як є;
 *   2. згортання діакритики ("obrączka" -> "obraczka");
 *   3. конверсія помилкової розкладки (QWERTY <-> ЙЦУКЕН);
 *   4. трансліт кирилиці латиницею (для назв товарів: "гладиатор" -> "gladiator");
 *   5. обрізаний корінь довгих слів ("wisiorki" -> "wisi*" через префіксний матч).
 *
 * Плюс: історія останніх 5 запитів браузера в порожній панелі (localStorage)
 * і лог запитів у dataLayer (event: predictive_search) — з нього в GA4
 * видно нульові запити, якими поповнювати словники.
 *
 * Особливість Shopify: suggest-ендпоінт НЕ матчить теги на перекладених
 * локалях (/de-de/, /fr-fr/…), хоча повна сторінка /search матчить їх
 * скрізь. Тому останній прохід конвеєра повторює запит до англійської
 * версії того ж ринку (/de-de/ -> /en-de/ — та сама валюта), а лінки
 * результатів переписуються назад у поточну локаль.
 *
 * Словникові варіанти (польська, кирилиця, трансліт типів/матеріалів/стилів)
 * живуть НЕ тут, а в тегах товарів `s-*` — їх матчить сам Shopify.
 * Індексатор: scripts/build_search_tags.py у проєкті магазину.
 */

const SEARCH_CONSTANTS = {
	DEBOUNCE_DELAY: 300,
	MIN_CHARS: 2,
	HISTORY_KEY: 'reytel-search-history',
	HISTORY_MAX: 5,
	SELECTOR: {
		SEARCH_INPUT: 'input[type="search"]',
		PANEL: '[data-search-panel]',
		RESULTS_SLOT: '[data-search-results-slot]',
		SEARCH_RESULTS: '[data-predictive-search-results]',
		ZERO_STATE: '[data-search-zero-state]',
		ZERO_EMPTY: '[data-search-zero-empty]',
		FALLBACK_NOTE: '[data-search-fallback-note]',
		SEARCH_BUTTON: '[data-search-button]',
		CHIP: '[data-search-chip]',
		HISTORY_BLOCK: '[data-search-history]',
		HISTORY_LIST: '[data-search-history-list]',
		HISTORY_CLEAR: '[data-search-history-clear]'
	}
};

const LAYOUT_EN_TO_RU = {
	q: 'й', w: 'ц', e: 'у', r: 'к', t: 'е', y: 'н', u: 'г', i: 'ш', o: 'щ',
	p: 'з', '[': 'х', ']': 'ъ', a: 'ф', s: 'ы', d: 'в', f: 'а', g: 'п',
	h: 'р', j: 'о', k: 'л', l: 'д', ';': 'ж', "'": 'э', z: 'я', x: 'ч',
	c: 'с', v: 'м', b: 'и', n: 'т', m: 'ь', ',': 'б', '.': 'ю'
};
const LAYOUT_RU_TO_EN = Object.fromEntries(
	Object.entries(LAYOUT_EN_TO_RU).map(([en, ru]) => [ru, en])
);

const TRANSLIT_CYR = {
	а: 'a', б: 'b', в: 'v', г: 'g', ґ: 'g', д: 'd', е: 'e', є: 'ye',
	ж: 'zh', з: 'z', и: 'i', і: 'i', ї: 'yi', й: 'j', к: 'k', л: 'l',
	м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u',
	ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'shch', ь: '', ъ: '',
	ю: 'yu', я: 'ya', э: 'e', ы: 'y', ё: 'e'
};

function searchNormalize(query) {
	return query.toLowerCase().replace(/\s+/g, ' ').trim();
}

/*
 * Згортання діакритики латиниці. Shopify сам згортає комбіновані знаки
 * (ä→a, é→e, ř→r), але НЕ окремі літери на кшталт польської ł —
 * цей прохід закриває і їх.
 */
const FOLD_EXTRA = { ł: 'l', ø: 'o', đ: 'd', ß: 'ss', æ: 'ae', œ: 'oe' };

function foldDiacritics(text) {
	let out = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
	out = out.replace(/[łøđßæœ]/g, (ch) => FOLD_EXTRA[ch] || ch);
	return out;
}

function convertByMap(text, map) {
	let out = '';
	for (const ch of text) out += map[ch] !== undefined ? map[ch] : ch;
	return out;
}

function truncateRoots(query) {
	return query
		.split(' ')
		.map((word) => {
			if (word.length < 6 || /\d/.test(word)) return word;
			return word.slice(0, Math.max(4, word.length - 3));
		})
		.join(' ');
}

/* Черга варіантів запиту для конвеєра, без дублів. */
function buildQueryPasses(rawQuery) {
	const normalized = searchNormalize(rawQuery);
	const passes = [normalized];
	const hasCyrillic = /[а-яёіїєґ]/i.test(normalized);
	const hasLatin = /[a-z]/i.test(normalized);

	const folded = foldDiacritics(normalized);
	if (folded !== normalized) passes.push(folded);

	if (hasLatin && !hasCyrillic) {
		passes.push(convertByMap(normalized, LAYOUT_EN_TO_RU));
	}
	if (hasCyrillic) {
		passes.push(convertByMap(normalized, LAYOUT_RU_TO_EN));
		passes.push(convertByMap(normalized, TRANSLIT_CYR));
	}
	const truncated = truncateRoots(normalized);
	if (truncated !== normalized) passes.push(truncated);

	return [...new Set(passes)].filter((pass) => pass.length >= SEARCH_CONSTANTS.MIN_CHARS);
}

/* Історія запитів цього браузера (для порожньої панелі). */
function readSearchHistory() {
	try {
		const parsed = JSON.parse(localStorage.getItem(SEARCH_CONSTANTS.HISTORY_KEY));
		return Array.isArray(parsed) ? parsed.filter((q) => typeof q === 'string') : [];
	} catch (error) {
		return [];
	}
}

function saveSearchHistory(query) {
	const normalized = searchNormalize(query);
	if (normalized.length < SEARCH_CONSTANTS.MIN_CHARS) return;
	try {
		const history = [normalized]
			.concat(readSearchHistory().filter((q) => q !== normalized))
			.slice(0, SEARCH_CONSTANTS.HISTORY_MAX);
		localStorage.setItem(SEARCH_CONSTANTS.HISTORY_KEY, JSON.stringify(history));
	} catch (error) {
		/* приватний режим — історія просто не ведеться */
	}
}

function clearSearchHistory() {
	try {
		localStorage.removeItem(SEARCH_CONSTANTS.HISTORY_KEY);
	} catch (error) {
		/* ignore */
	}
}

/* Лог запитів у GA4 через dataLayer: джерело «нульових» запитів для словників. */
function logSearchEvent(rawQuery, effectiveQuery, productsCount) {
	try {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'predictive_search',
			search_query: rawQuery,
			search_effective_query: effectiveQuery || rawQuery,
			search_results: productsCount
		});
	} catch (error) {
		/* ignore */
	}
}

class PredictiveSearch extends HTMLElement {
	constructor() {
		super();
		this.cachedResults = new Map();
		this.requestSeq = 0;
		this.effectiveQuery = null;
		this.lastRawQuery = null;

		this.init();
	}

	init() {
		this.input = this.querySelector(SEARCH_CONSTANTS.SELECTOR.SEARCH_INPUT);
		this.button = this.querySelector(SEARCH_CONSTANTS.SELECTOR.SEARCH_BUTTON);
		this.form = this.querySelector('form');
		this.panel = this.querySelector(SEARCH_CONSTANTS.SELECTOR.PANEL);
		this.resultsSlot = this.querySelector(SEARCH_CONSTANTS.SELECTOR.RESULTS_SLOT);
		this.zeroState = this.querySelector(SEARCH_CONSTANTS.SELECTOR.ZERO_STATE);
		this.zeroEmpty = this.querySelector(SEARCH_CONSTANTS.SELECTOR.ZERO_EMPTY);
		this.fallbackNote = this.querySelector(SEARCH_CONSTANTS.SELECTOR.FALLBACK_NOTE);
		this.sectionId = 'predictive-search';

		if (!this.input || !this.panel || !this.resultsSlot) {
			console.warn('Required elements not found for predictive search');
			return;
		}

		this.setupEventListeners();
	}

	setupEventListeners() {
		const debouncedSearch = debounce(
			() => this.handleSearchInput(),
			SEARCH_CONSTANTS.DEBOUNCE_DELAY
		).bind(this);

		this.input.addEventListener('input', debouncedSearch);
		this.input.addEventListener('focus', () => this.handleSearchInput());

		this.addEventListener('click', (event) => {
			const clearButton = event.target.closest(SEARCH_CONSTANTS.SELECTOR.HISTORY_CLEAR);
			if (clearButton) {
				event.preventDefault();
				clearSearchHistory();
				this.renderHistory();
				return;
			}

			const chip = event.target.closest(SEARCH_CONSTANTS.SELECTOR.CHIP);
			if (chip) {
				event.preventDefault();
				this.input.value = chip.getAttribute('data-search-chip');
				this.input.focus();
				this.handleSearchInput();
				return;
			}

			/* перехід на товар/підказку/категорію з панелі — запит вдалий, у історію */
			if (event.target.closest('[data-search-results-slot] a')) {
				saveSearchHistory(this.effectiveQuery || this.lastRawQuery || '');
			}
		});

		if (this.form) {
			this.form.addEventListener('submit', () => {
				const raw = searchNormalize(this.input.value);
				if (this.effectiveQuery && raw === this.lastRawQuery && this.effectiveQuery !== raw) {
					this.input.value = this.effectiveQuery;
				}
				saveSearchHistory(this.input.value);
			});
		}
	}

	renderHistory() {
		const block = this.querySelector(SEARCH_CONSTANTS.SELECTOR.HISTORY_BLOCK);
		const list = this.querySelector(SEARCH_CONSTANTS.SELECTOR.HISTORY_LIST);
		if (!block || !list) return;

		const history = readSearchHistory();
		if (!history.length) {
			block.style.display = 'none';
			list.innerHTML = '';
			return;
		}

		list.innerHTML = '';
		for (const query of history) {
			const chip = document.createElement('a');
			chip.className = 'search-panel__chip';
			chip.href = '#';
			chip.setAttribute('data-search-chip', query);
			chip.textContent = query;
			list.appendChild(chip);
		}
		block.style.display = '';
	}

	handleSearchInput() {
		const searchTerm = searchNormalize(this.input.value);

		if (searchTerm.length < SEARCH_CONSTANTS.MIN_CHARS) {
			if (this.button) this.button.disabled = true;
			this.effectiveQuery = null;
			this.showZeroState(false);
			return;
		}
		if (this.button) this.button.disabled = false;
		this.runSearchPipeline(searchTerm);
	}

	/*
	 * "/fr-fr/search/suggest" -> "/en-fr/search/suggest"; null, коли поточна
	 * локаль і так англійська (там теги матчаться без обхідного шляху).
	 */
	englishSuggestUrl() {
		const url = window.routes.predictive_search_url;
		const match = url.match(/^\/([a-z]{2})-([a-z]{2})(\/.+)$/);
		if (!match || match[1] === 'en') return null;
		return `/en-${match[2]}${match[3]}`;
	}

	async runSearchPipeline(rawQuery) {
		const seq = ++this.requestSeq;
		this.lastRawQuery = rawQuery;
		const queryPasses = buildQueryPasses(rawQuery);

		const passes = queryPasses
			.map((query, index) => ({ query, endpoint: null, isFallback: index > 0 }));

		const englishEndpoint = this.englishSuggestUrl();
		if (englishEndpoint) {
			for (const query of queryPasses) {
				passes.push({ query, endpoint: englishEndpoint, isFallback: true });
			}
		}

		let firstNonEmpty = null;
		for (const pass of passes) {
			let result;
			try {
				result = await this.fetchResults(pass.query, pass.endpoint);
			} catch (error) {
				continue;
			}
			if (seq !== this.requestSeq) return;

			if (result.productsCount > 0) {
				this.effectiveQuery = pass.query;
				this.renderResults(result.markup, pass.isFallback, Boolean(pass.endpoint));
				logSearchEvent(rawQuery, pass.query, result.productsCount);
				return;
			}
			if (!firstNonEmpty && result.collectionsCount + result.queriesCount > 0) {
				firstNonEmpty = result;
			}
		}

		if (seq !== this.requestSeq) return;
		this.effectiveQuery = null;
		logSearchEvent(rawQuery, null, 0);
		if (firstNonEmpty) {
			this.renderResults(firstNonEmpty.markup, false);
		} else {
			this.showZeroState(true);
		}
	}

	async fetchResults(searchTerm, endpoint) {
		const requestUrl = endpoint || window.routes.predictive_search_url;
		const queryKey = `${requestUrl}|${searchTerm}`;
		if (this.cachedResults.has(queryKey)) {
			return this.cachedResults.get(queryKey);
		}

		const searchParams = new URLSearchParams({
			q: searchTerm,
			'resources[type]': 'product,collection,query',
			'resources[limit]': '8',
			section_id: this.sectionId
		});

		const response = await fetch(`${requestUrl}?${searchParams}`);
		if (!response.ok) {
			throw new Error(`Search request failed: ${response.status}`);
		}

		const text = await response.text();
		const doc = new DOMParser().parseFromString(text, 'text/html');
		const resultsRoot = doc.querySelector(SEARCH_CONSTANTS.SELECTOR.SEARCH_RESULTS);
		if (!resultsRoot) {
			throw new Error('Unexpected search response');
		}

		const result = {
			markup: resultsRoot.innerHTML,
			productsCount: parseInt(resultsRoot.getAttribute('data-products-count') || '0', 10),
			collectionsCount: parseInt(resultsRoot.getAttribute('data-collections-count') || '0', 10),
			queriesCount: parseInt(resultsRoot.getAttribute('data-queries-count') || '0', 10)
		};
		this.cachedResults.set(queryKey, result);
		return result;
	}

	renderResults(markup, isFallbackPass, fromEnglishEndpoint) {
		this.resultsSlot.innerHTML = markup;

		/*
		 * Результати з /en-XX/ ведуть на англійські URL — повертаємо лінки
		 * в поточну локаль (хендли однакові, повний /search матчить теги
		 * на будь-якій локалі, тож і "view all" безпечно переписувати).
		 */
		if (fromEnglishEndpoint) {
			const currentPrefix = this.localePrefix();
			if (currentPrefix) {
				for (const link of this.resultsSlot.querySelectorAll('a[href^="/en-"]')) {
					link.setAttribute(
						'href',
						link.getAttribute('href').replace(/^\/en-[a-z]{2}/, currentPrefix)
					);
				}
			}
		}

		if (this.zeroState) this.zeroState.style.display = 'none';
		if (this.fallbackNote) {
			this.fallbackNote.style.display = isFallbackPass ? '' : 'none';
		}
		this.openPanel();
	}

	/* "/fr-fr" з routes, або null на кореневій (англійській) локалі. */
	localePrefix() {
		const match = window.routes.predictive_search_url.match(/^\/([a-z]{2}-[a-z]{2})\//);
		return match ? `/${match[1]}` : null;
	}

	showZeroState(afterEmptySearch) {
		this.resultsSlot.innerHTML = '';
		if (this.fallbackNote) this.fallbackNote.style.display = 'none';
		if (this.zeroState) {
			this.zeroState.style.display = '';
			if (this.zeroEmpty) {
				this.zeroEmpty.style.display = afterEmptySearch ? '' : 'none';
				/*
				 * Повний /search уміє те, чого suggest не вміє (пошук за SKU,
				 * довгі числа) — тому нуль у панелі не вирок, даємо лінк.
				 */
				const zeroLink = this.zeroEmpty.querySelector('[data-search-zero-link]');
				if (zeroLink && this.form && this.lastRawQuery) {
					const params = new URLSearchParams({
						q: this.lastRawQuery,
						type: 'product',
						'options[prefix]': 'last'
					});
					zeroLink.setAttribute('href', `${this.form.getAttribute('action')}?${params}`);
				}
			}
			this.renderHistory();
		}
		this.openPanel();
	}

	openPanel() {
		this.panel.style.display = '';
	}

	closePanel() {
		this.panel.style.display = 'none';
	}
}

customElements.define('predictive-search', PredictiveSearch);
